/**
 * RoundaboutsProgressRing — small at-a-glance progress dial.
 *
 * Calls /api/roundabouts/me/progress and renders a subtle SVG ring
 * showing X/9 cards understood. Used in:
 *  - /roundabouts hub header
 *  - /learn entry tile
 *  - in-homework trigger card header
 *
 * `size` — diameter px. `tone` — 'emerald' (default) or 'white' for dark BGs.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const TONE = {
  emerald: { track: "rgba(16,185,129,0.15)", fill: "#10b981", text: "text-emerald-700 dark:text-emerald-400" },
  white:   { track: "rgba(255,255,255,0.15)", fill: "#fff",     text: "text-white" },
  slate:   { track: "rgba(148,163,184,0.20)", fill: "#10b981", text: "text-slate-700 dark:text-slate-200" },
};

export default function RoundaboutsProgressRing({ size = 36, stroke = 4, tone = "emerald" }) {
  const [data, setData] = useState({ understood_count: 0, total: 9, percent: 0 });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/roundabouts/me/progress`, { withCredentials: true });
        if (!cancelled) {
          setData(data);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded) return null;

  const palette = TONE[tone] || TONE.emerald;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (data.percent / 100) * c;

  return (
    <div
      data-testid="roundabouts-progress-ring"
      className="inline-flex items-center justify-center relative"
      style={{ width: size, height: size }}
      aria-label={`${data.understood_count} of ${data.total} roundabout cards understood`}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={palette.track} strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={palette.fill}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
      </svg>
      <span className={`absolute text-[10px] font-extrabold ${palette.text} leading-none`}>
        {data.understood_count}/{data.total}
      </span>
    </div>
  );
}

/**
 * RoundaboutsProgressCard — calm growth message above the library.
 */
export function RoundaboutsProgressCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/roundabouts/me/progress`, { withCredentials: true });
        if (!cancelled) setData(data);
      } catch {
        /* hide silently */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const { understood_count: c, total } = data;
  let copy;
  if (c === 0) copy = "Your roundabout learning starts here. One understanding point at a time.";
  else if (c < total / 2) copy = `You've understood ${c} of ${total} roundabout skills. Building nicely.`;
  else if (c < total) copy = `Strong progress — ${c} of ${total} understood. Keep going.`;
  else copy = `All ${total} roundabout skills understood. You've built calm confidence — refreshers will appear when useful.`;

  return (
    <div
      data-testid="roundabouts-progress-card"
      className="bg-gradient-to-br from-emerald-500/10 to-blue-500/5 border border-emerald-200 dark:border-emerald-900/40 rounded-2xl p-4 flex items-center gap-4"
    >
      <RoundaboutsProgressRing size={56} stroke={6} tone="slate" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-400 mb-0.5">Your growth</p>
        <p className="text-sm font-semibold text-slate-900 dark:text-white leading-snug">{copy}</p>
      </div>
    </div>
  );
}
